import React from 'react';
import { useTranslation } from 'react-i18next';

import Tracked from '~/components/Tracked';
import type { MenuItemProps } from '~/legacy/types';
import { logoIcons } from '~/legacy/utils';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import CategoryList from './CategoryList';
import MenuLink from './MenuLink';

const MenuItem = ({
  link,
  name,
  content,
  slug,
  track,
  pagePosition
}: MenuItemProps) => {
  const { t } = useTranslation();

  return (
    <li className="main-header__nav__top-list-item">
      <Tracked.ExternalLink
        name={name as TrackedButtonClickName}
        href={link}
        variant="none"
        className="main-header__nav__top-list-item--link flex"
        pagePosition="Header"
      >
        {name}
      </Tracked.ExternalLink>
      {content?.render ? (
        <div className="main-header__nav__sub-nav-list">
          <div className="main-header__nav__sub-nav-list--offset"></div>
          <div className="main-header__nav__sub-nav-list--container">
            <div className="content-width pt-8 pb-10 max-h-[calc(100vh-65px)]">
              <div className="flex w-full flex-col">
                {/* Footwear SubMenu Filters */}
                {content?.footwear?.filters.length > 0 && (
                  <div className="flex w-full gap-6">
                    <div className="text-title-sm">
                      {content?.footwear?.title}
                    </div>
                    <div className="flex gap-4">
                      {content?.footwear?.filters.map((item: MenuItemProps) => (
                        <React.Fragment key={`${item?.title}/${item?.link}`}>
                          <MenuLink
                            title={item?.title}
                            link={item?.link}
                            track={track}
                            pagePosition={pagePosition}
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-6 items-center mt-6 mb-4">
                  <div className="text-title-sm">{t('featuredBrands')}</div>
                  {content?.footwear?.viewAll && (
                    <MenuLink
                      link={content?.footwear.viewAll.link}
                      title={t('viewAllBrands')}
                    />
                  )}
                </div>

                {content?.footwear?.featuredBrands && (
                  <div
                    className={`flex w-full justify-between ${
                      content?.footwear?.featuredBrands.path === 'clothing'
                        ? 'mr-10'
                        : ''
                    }`}
                  >
                    {content?.footwear?.featuredBrands.children.map(
                      (item: any, i: number) => {
                        const { children, icon, name, viewAll, path } = item;

                        return (
                          <div key={i} className="subNavFeaturedBrands">
                            {icon ? (
                              <Tracked.ExternalLink
                                variant="none"
                                href={
                                  viewAll
                                    ? `${path}${viewAll?.link || ''}`
                                    : undefined
                                }
                                pagePosition="Header"
                                name={name as TrackedButtonClickName}
                              >
                                <img
                                  alt={name}
                                  src={logoIcons[icon]}
                                  loading="lazy"
                                  className="subNavFeaturedBrands__icon"
                                />
                              </Tracked.ExternalLink>
                            ) : (
                              slug
                            )}
                            {children && (
                              <>
                                {viewAll && (
                                  <Tracked.ExternalLink
                                    href={`${path}${viewAll?.link || ''}`}
                                    className="text-title-xs flex items-center h-6 pt-1"
                                    variant="none"
                                    pagePosition="Header"
                                    name={
                                      viewAll.label as TrackedButtonClickName
                                    }
                                  >
                                    {viewAll.label}
                                  </Tracked.ExternalLink>
                                )}
                                {children?.map(
                                  (brand: MenuItemProps, i: number) => (
                                    <Tracked.ExternalLink
                                      className="subNavFeaturedBrands__brand"
                                      key={`sub_brand_${i}`}
                                      href={`${path}${brand?.slug}/`}
                                      pagePosition="Header"
                                      variant="none"
                                      name={
                                        brand.name as TrackedButtonClickName
                                      }
                                    >
                                      {brand?.name}
                                    </Tracked.ExternalLink>
                                  )
                                )}
                              </>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
                {content?.footwear?.categories && (
                  <div className="main-header__sub-nav-list__extended-category">
                    {content?.footwear?.categories?.sport && (
                      <CategoryList
                        title={content?.footwear?.categories?.sport.title}
                        children={content?.footwear?.categories?.sport.children}
                        parentLink={content?.footwear?.categories?.sport.link}
                      />
                    )}

                    {content?.footwear?.categories?.style && (
                      <CategoryList
                        title={content?.footwear?.categories?.style.title}
                        children={content?.footwear?.categories?.style.children}
                        parentLink={content?.footwear?.categories?.style.link}
                      />
                    )}

                    {content?.footwear?.categories?.material && (
                      <CategoryList
                        title={content?.footwear?.categories?.material.title}
                        children={
                          content?.footwear?.categories?.material.children
                        }
                        parentLink={
                          content?.footwear?.categories?.material.link
                        }
                      />
                    )}

                    {content?.footwear?.categories?.colour && (
                      <CategoryList
                        title={content?.footwear?.categories?.colour.title}
                        children={
                          content?.footwear?.categories?.colour.children
                        }
                        parentLink={content?.footwear?.categories?.colour.link}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
};

export default MenuItem;
